import { Button } from '@synoptic/ui-kit/button/button.js';
import { IconButton } from '@synoptic/ui-kit/button/icon-button.js';
import { ArrowLeftIcon } from '@synoptic/ui-kit/icons/react/arrow-left.js';
import { CheckCircleIcon } from '@synoptic/ui-kit/icons/react/check-circle.js';
import { PlaySolidIcon } from '@synoptic/ui-kit/icons/react/play-solid.js';
import { XmarkCircleIcon } from '@synoptic/ui-kit/icons/react/xmark-circle.js';
import {
  ModalRoot,
  ModalTrigger,
  ModalContent,
  ModalHeader,
  ModalTitle,
} from '@synoptic/ui-kit/modal/modal.js';
import { Textarea } from '@synoptic/ui-kit/textarea/textarea.js';
import { useRef, useState } from 'react';
import { AiFilterFormValues } from '../types';
import { useTestAiFilter } from '../use-test-ai-filter';
import { backButton } from '../ai-filters.css';
import {
  testFilterButton,
  testFilterContainer,
  testFilterStatus,
  textareaContainer,
  textareaFooter,
  textareaInput,
  testFilterDescription,
  modalFooterWithFlexEnd,
} from './manage-filter.css';

const TestAIFilterStatus = ({ passed }: { passed: boolean }) => {
  return (
    <div className={testFilterStatus}>
      {passed ? (
        <CheckCircleIcon width={24} height={24} />
      ) : (
        <XmarkCircleIcon width={24} height={24} />
      )}
      <p>
        {passed ? 'Post matches your filter' : 'Post doesn’t match your filter'}
      </p>
    </div>
  );
};

type TestAIFilterModalProps = {
  getFilterValues: () => AiFilterFormValues;
  disabled: boolean;
};

export const TestAIFilterModal = ({
  getFilterValues,
  disabled,
}: TestAIFilterModalProps) => {
  const [text, setText] = useState('');
  const formValues = getFilterValues();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { testFilter, result, loading } = useTestAiFilter(
    {
      prompt: formValues.prompt,
      model: formValues.model,
      passValue: formValues.passValue!,
    },
    text,
  );
  const [isOpen, setIsOpen] = useState(false);
  return (
    <ModalRoot open={isOpen} onOpenChange={setIsOpen}>
      <ModalTrigger asChild>
        <Button
          className={testFilterButton}
          size="small"
          disabled={disabled}
          startIcon={<PlaySolidIcon />}
          variant="outline"
        >
          Run test for this filter
        </Button>
      </ModalTrigger>
      <ModalContent
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          textareaRef?.current?.focus();
        }}
      >
        <ModalHeader withoutCloseIcon>
          <IconButton
            onClick={() => setIsOpen(false)}
            className={backButton}
            variant="ghost"
            size="small"
          >
            <ArrowLeftIcon style={{ width: 24, height: 24 }} />
          </IconButton>
          <ModalTitle>Run test for this filter</ModalTitle>
        </ModalHeader>
        <div className={testFilterContainer}>
          <p className={testFilterDescription}>
            Test your filter by entering the text of a post. We&apos;ll check if
            it matches your filter criteria.
          </p>
          <div
            onClick={() => {
              textareaRef.current?.focus();
            }}
            className={textareaContainer}
          >
            <Textarea
              autoResize
              ref={textareaRef}
              autoFocus
              rows={3}
              value={text}
              containerClassName={textareaInput}
              onChange={(e) => setText(e.target.value)}
              placeholder="Type or paste the post content here"
            />
            <div className={textareaFooter}>
              {result !== undefined && <TestAIFilterStatus passed={result} />}

              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  testFilter();
                }}
                className={testFilterButton}
                disabled={!text}
                loading={loading}
                variant="primary"
                size="small"
              >
                Run test
              </Button>
            </div>
          </div>
        </div>
        <div className={modalFooterWithFlexEnd}>
          <Button onClick={() => setIsOpen(false)} variant="primary">
            Done
          </Button>
        </div>
      </ModalContent>
    </ModalRoot>
  );
};
